/**
 * One threshold card for the selector form.
 */

import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { MouseEvent, ReactNode } from 'react';

import type {
  ThresholdVariableModel,
  UIThresholdModel,
} from './catalog-transform';
import { ThresholdFunctionPreview } from './threshold-function-preview';

// -- type definitions --

export interface ThresholdSelectorCardProps {
  readonly threshold: UIThresholdModel;
  readonly selected: boolean;
  readonly detailsOpen: boolean;
  readonly onToggle: (thresholdId: number) => void;
  readonly onToggleDetails: (thresholdId: number) => void;
  readonly onEditRequest?: (threshold: UIThresholdModel) => void;
  readonly disabled?: boolean;
}

// -- functions --

function getAccentColor(threshold: UIThresholdModel): string {
  return threshold.defaultStyle?.color ?? '#2563eb';
}

function translateVariableRole(role: ThresholdVariableModel['role']): string {
  switch (role) {
    case 'independent':
      return 'variable indépendante';
    case 'dependent':
      return 'variable dépendante';
    case 'auxiliary':
      return 'variable auxiliaire';
    case 'time':
      return 'variable temporelle';
    default:
      return role;
  }
}

function translateDataType(
  dataType: ThresholdVariableModel['dataType']
): string {
  switch (dataType) {
    case 'number':
      return 'numérique';
    case 'datetime':
      return 'date/heure';
    case 'category':
      return 'catégorie';
    case 'vector':
      return 'vecteur';
    case 'boolean':
      return 'booléen';
    case 'string':
      return 'texte';
    default:
      return dataType;
  }
}

function formatValue(value: unknown): string {
  if (
    typeof value === 'number' ||
    typeof value === 'string' ||
    typeof value === 'boolean'
  ) {
    return `${value}`;
  }

  if (value == null) {
    return '—';
  }

  return JSON.stringify(value);
}

function translateBoundKind(boundKind: UIThresholdModel['boundKind']): string {
  return boundKind === 'maximum' ? 'Borne haute' : 'Borne basse';
}

function renderTableWrapper(content: ReactNode): ReactNode {
  return <Box sx={{ width: '100%', overflowX: 'auto' }}>{content}</Box>;
}

function renderKeyValueTable(
  rows: ReadonlyArray<{ readonly key: string; readonly value: unknown }>,
  ariaLabel: string
): ReactNode {
  return renderTableWrapper(
    <Table
      size="small"
      aria-label={ariaLabel}
      sx={{
        width: '100%',
        tableLayout: 'fixed',
        '& th, & td': {
          py: 0.75,
          wordBreak: 'break-word',
        },
      }}
    >
      <TableHead>
        <TableRow>
          <TableCell>Paramètre</TableCell>
          <TableCell>Valeur</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={`${ariaLabel}-${row.key}`}>
            <TableCell>{row.key}</TableCell>
            <TableCell>{formatValue(row.value)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function buildInfoTooltip(threshold: UIThresholdModel): ReactNode {
  return (
    <Stack spacing={0.5} sx={{ maxWidth: 280 }}>
      <Typography variant="subtitle2">Informations contextuelles</Typography>
      <Typography variant="caption">
        {threshold.description || 'Aucune description complémentaire.'}
      </Typography>
      {threshold.variableDetails.map((variable) => (
        <Typography
          key={`${threshold.id}-${variable.variableName}`}
          variant="caption"
        >
          {variable.variableName} • {translateVariableRole(variable.role)} •{' '}
          {translateDataType(variable.dataType)}
        </Typography>
      ))}
    </Stack>
  );
}

function renderTechnicalBreakdown(
  threshold: UIThresholdModel
): ReactNode | null {
  const parameters = threshold.rawParameters;

  if (threshold.type === 'piecewise' && Array.isArray(parameters.segments)) {
    return renderTableWrapper(
      <Table
        size="small"
        aria-label={`Segments de ${threshold.label}`}
        sx={{
          width: '100%',
          tableLayout: 'fixed',
          '& th, & td': {
            py: 0.75,
            wordBreak: 'break-word',
          },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell>Début</TableCell>
            <TableCell>Fin</TableCell>
            <TableCell>Expression</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {parameters.segments.map((segment, index) => {
            if (typeof segment !== 'object' || segment === null) {
              return null;
            }

            const typedSegment = segment as {
              lower?: unknown;
              upper?: unknown;
              expression?: unknown;
            };

            return (
              <TableRow key={`${threshold.id}-segment-${index}`}>
                <TableCell>{formatValue(typedSegment.lower)}</TableCell>
                <TableCell>{formatValue(typedSegment.upper)}</TableCell>
                <TableCell>{formatValue(typedSegment.expression)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  }

  if (threshold.type === 'polynomial') {
    const coefficients = Array.isArray(parameters.coefficients)
      ? parameters.coefficients
      : Array.isArray(parameters.coeffs)
      ? parameters.coeffs
      : [];

    return renderTableWrapper(
      <Table
        size="small"
        aria-label={`Coefficients de ${threshold.label}`}
        sx={{
          width: '100%',
          tableLayout: 'fixed',
          '& th, & td': {
            py: 0.75,
            wordBreak: 'break-word',
          },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell>Degré</TableCell>
            <TableCell>Coefficient</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {coefficients.map((coefficient, index) => {
            const degree = coefficients.length - index - 1;
            return (
              <TableRow key={`${threshold.id}-coef-${degree}`}>
                <TableCell>{degree}</TableCell>
                <TableCell>{formatValue(coefficient)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  }

  if (threshold.type === 'circle') {
    return renderKeyValueTable(
      [
        { key: 'Centre X', value: parameters.center_x ?? parameters.cx ?? 0 },
        { key: 'Centre Y', value: parameters.center_y ?? parameters.cy ?? 0 },
        { key: 'Rayon', value: parameters.radius ?? 0 },
      ],
      `Géométrie de ${threshold.label}`
    );
  }

  if (threshold.type === 'rectangle') {
    return renderKeyValueTable(
      [
        {
          key: 'X min',
          value: parameters.x_min ?? parameters.min_x ?? parameters.left,
        },
        {
          key: 'X max',
          value: parameters.x_max ?? parameters.max_x ?? parameters.right,
        },
        {
          key: 'Y min',
          value: parameters.y_min ?? parameters.min_y ?? parameters.bottom,
        },
        {
          key: 'Y max',
          value: parameters.y_max ?? parameters.max_y ?? parameters.top,
        },
      ],
      `Bornes de ${threshold.label}`
    );
  }

  const genericRows = Object.entries(parameters).map(([key, value]) => ({
    key,
    value,
  }));

  return genericRows.length > 0
    ? renderKeyValueTable(genericRows, `Paramètres de ${threshold.label}`)
    : null;
}

/**
 * Renders one selectable threshold entry close to the original PoC card layout.
 */
export function ThresholdSelectorCard({
  threshold,
  selected,
  detailsOpen,
  onToggle,
  onToggleDetails,
  onEditRequest,
  disabled = false,
}: ThresholdSelectorCardProps) {
  const accentColor = getAccentColor(threshold);
  const technicalBreakdown = renderTechnicalBreakdown(threshold);

  const stopPropagation = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
  };

  const handleDetailsClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onToggleDetails(threshold.id);
  };

  const handleEditClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onEditRequest?.(threshold);
  };

  return (
    <Card
      variant="outlined"
      onClick={() => onToggle(threshold.id)}
      aria-pressed={selected}
      sx={{
        borderRadius: '18px',
        width: '100%',
        minWidth: 0,
        overflow: 'hidden',
        cursor: disabled ? 'default' : 'pointer',
        backgroundColor: '#f7f7f8',
        borderWidth: selected ? 2 : 1,
        borderColor: selected ? '#63c7ff' : 'rgba(15, 23, 42, 0.12)',
        boxShadow: 'none',
        transition:
          'background-color 160ms ease, border-color 160ms ease, box-shadow 160ms ease',
        '&:hover': {
          backgroundColor: '#f2f8fc',
          borderColor: selected ? '#63c7ff' : alpha(accentColor, 0.5),
        },
      }}
    >
      <CardContent sx={{ overflowX: 'hidden', px: 1.5, py: 1.5 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '42px minmax(0, 1fr) 34px',
            columnGap: 1,
            alignItems: 'start',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 0.15 }}>
            <Switch
              checked={selected}
              disabled={disabled}
              onClick={stopPropagation}
              onChange={(event) => {
                event.stopPropagation();
                onToggle(threshold.id);
              }}
              inputProps={{ 'aria-label': threshold.label }}
              sx={{ m: 0 }}
            />
          </Box>

          <Stack spacing={1.5} sx={{ minWidth: 0 }}>
            <Stack spacing={0.75} sx={{ minWidth: 0 }}>
              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                flexWrap="wrap"
                alignItems="center"
              >
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  sx={{ minWidth: 0 }}
                >
                  {threshold.label}
                </Typography>
                <Chip size="small" label={threshold.typeLabel} />
                <Chip
                  size="small"
                  variant="outlined"
                  label={translateBoundKind(threshold.boundKind)}
                />
                {threshold.isLegacy ? (
                  <Chip size="small" color="warning" label="Version obsolète" />
                ) : null}
              </Stack>

              <Typography variant="body2" color="text.secondary">
                {threshold.parameterSummary}
              </Typography>
            </Stack>

            <ThresholdFunctionPreview threshold={threshold} height={112} />

            <Button
              variant="outlined"
              fullWidth
              onClick={handleDetailsClick}
              startIcon={detailsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{
                py: 1,
                borderColor: '#90caf9',
                color: '#1976d2',
                fontWeight: 500,
                letterSpacing: 0.2,
                textTransform: 'uppercase',
              }}
            >
              {detailsOpen
                ? 'Masquer les spécifications techniques'
                : 'Spécifications techniques'}
            </Button>

            <Collapse in={detailsOpen} onClick={stopPropagation}>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Stack spacing={1.5} sx={{ minWidth: 0 }}>
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Variables requises
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={0.75}
                      useFlexGap
                      flexWrap="wrap"
                      mt={0.75}
                    >
                      {threshold.variables.map((variable) => (
                        <Chip
                          key={`${threshold.id}-${variable}`}
                          size="small"
                          variant="outlined"
                          label={variable}
                        />
                      ))}
                    </Stack>
                  </Box>

                  {technicalBreakdown ? (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Détails du modèle
                      </Typography>
                      {technicalBreakdown}
                    </Box>
                  ) : null}

                  <Divider flexItem />

                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Description
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {threshold.description || 'Aucune description fournie.'}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Collapse>
          </Stack>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.5,
              pt: 0.35,
            }}
          >
            <Tooltip title="Modifier ce seuil" arrow placement="left-start">
              <span>
                <IconButton
                  size="small"
                  onClick={handleEditClick}
                  disabled={disabled || !onEditRequest}
                  aria-label={`Modifier ${threshold.label}`}
                  sx={{ color: 'text.secondary' }}
                >
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip
              title={buildInfoTooltip(threshold)}
              arrow
              placement="left-start"
            >
              <IconButton
                size="small"
                onClick={stopPropagation}
                aria-label={`Informations sur ${threshold.label}`}
                sx={{ color: 'text.secondary' }}
              >
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

export default ThresholdSelectorCard;
